import React, { useEffect } from 'react';
import { message } from 'components';
import { useAppointmentActionModalContext } from 'contexts/modal/AppointmentActionModalContext';
import type { ActionModalAppointmentType } from 'contexts/modal/types';
import type {
  ActionType,
  AgentType,
} from 'modals/AppointmentActionModal/types';
import {
  coopConfirmCancel,
  coopExternalConfirmCancel,
  clientExternalConfirmCancel,
} from 'api/AppointmentController';
import errorReporter from '@app/errorReporter';
import { useHistory } from 'react-router-dom';
import type { CoopCancelReasonType } from '../app/DashboardComponents/CoopCancelAppointmentComponent';

const useAppointmentActions = ({
  appointment,
  onConfirm,
  cancelFromTour,
  dashboardId,
}: {
  appointment: ActionModalAppointmentType;
  onConfirm: () => void;
  cancelFromTour?: (
    reason: CoopCancelReasonType,
    details: string,
    onConfirm?: () => void,
  ) => Promise<void>;
  dashboardId?: string;
}) => {
  const { openModal, closeModal } = useAppointmentActionModalContext();
  const [loading, setLoading] = React.useState(false);
  const history = useHistory();
  const useMakeModalCallback = (agent: AgentType, action: ActionType) =>
    React.useCallback(
      () =>
        openModal({
          agent,
          action,
          appointment,
          onConfirm,
          ...(cancelFromTour ? { cancelFromTour } : {}),
        }),
      [agent, action],
    );
  const coopConfirm = async () => {
    try {
      setLoading(true);
      if (dashboardId) {
        await coopExternalConfirmCancel({
          appointmentId: appointment.id,
          status: 'confirm',
          dashboardId,
        });
      } else {
        await coopConfirmCancel({
          appointmentId: appointment.id,
          status: 'confirm',
        });
      }
      message.success('Showing confirmed');
      onConfirm();
    } catch (err) {
      message.error('Error confirming showing');
      errorReporter.error(`Error confirming showing: ${err}`);
    } finally {
      setLoading(false);
    }
  };
  const coopCancel = useMakeModalCallback('coop', 'cancel');
  const coopDeny = useMakeModalCallback('coop', 'deny_coop');
  const laDeny = useMakeModalCallback('listing', 'deny');
  const laConfirm = useMakeModalCallback('listing', 'confirm');
  const laCancel = useMakeModalCallback('listing', 'cancel');
  const clientConfirm = async () => {
    try {
      setLoading(true);
      if (dashboardId) {
        await clientExternalConfirmCancel({
          appointmentId: appointment.id,
          status: 'confirm',
          dashboardId,
        });
      }
      message.success('Showing confirmed');
      onConfirm();
    } catch (err) {
      message.error('Error confirming showing');
      errorReporter.error(`Error confirming showing: ${err}`);
    } finally {
      setLoading(false);
    }
  };
  const clientCancel = useMakeModalCallback('client', 'cancel');
  const clientDeny = useMakeModalCallback('client', 'deny');

  useEffect(() => closeModal(), [history]);

  return {
    coopConfirm,
    coopCancel,
    coopDeny,
    laDeny,
    laConfirm,
    laCancel,
    loading,
    clientConfirm,
    clientCancel,
    clientDeny,
  };
};

export default useAppointmentActions;
